import { FileText, ExternalLink, ArrowLeft, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/logo.png";
import SEO from "@/components/SEO";

const Terms = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <SEO
        title="Terms of Service — GreyRock AI®"
        description="The terms that govern your use of GreyRock AI, including acceptable use, credits, account responsibilities, and the limits of forensic interpretation outputs."
        path="/terms"
      />
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(180deg, hsl(0 0% 10%) 0%, hsl(0 0% 5%) 100%)",
        }}
      />

      <div className="relative z-10 max-w-2xl mx-auto px-6 py-12 space-y-8">
        <div className="flex items-center gap-3 mb-8">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-cyan transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <img src={logo} alt="GreyRock AI" className="w-10 h-10 object-contain" />
          <span className="font-bold text-lg">
            <span className="text-foreground">Grey</span>
            <span className="text-cyan">Rock</span>
            <span className="text-foreground"> AI</span>
            <sup className="text-[10px] text-muted-foreground">&reg;</sup>
          </span>
        </div>

        <h1 className="text-3xl font-bold">
          <span className="text-foreground">Terms of </span>
          <span className="text-cyan">Service</span>
        </h1>

        <p className="text-muted-foreground leading-relaxed">
          This policy governs the use of GreyRock AI&reg;, a forensic text analysis platform owned and operated by Should I Studios LLC.
        </p>

        {/* Terms of Use */}
        <div className="rounded-xl border border-glass-border bg-charcoal-dark/50 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-cyan/10">
              <FileText className="w-6 h-6 text-cyan" />
            </div>
            <h2 className="font-semibold text-lg text-foreground">Terms of Use</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            Credits are non-refundable and usage is subject to fair use limits. By using GreyRock AI&reg;, you agree to our terms and conditions.
          </p>
          <a
            href="https://shouldistudios.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-cyan hover:underline"
          >
            Full Terms of Service <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        {/* Intellectual Property */}
        <div className="rounded-xl border border-glass-border bg-charcoal-dark/50 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-cyan/10">
              <Shield className="w-6 h-6 text-cyan" />
            </div>
            <h2 className="font-semibold text-lg text-foreground">Intellectual Property</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            All content, forensic card designs, and the brand name GreyRock AI&reg; are the exclusive intellectual property of the owners. Unauthorized reproduction or use of our forensic analysis templates is strictly prohibited.
          </p>
        </div>

        <footer className="pt-8 border-t border-glass-border text-center space-y-2">
          <p className="text-xs text-muted-foreground">
            &copy; 2026 GreyRock AI&reg;. All Rights Reserved.
          </p>
          <p className="text-xs text-muted-foreground">
            GreyRock AI&reg; is a brand of Should I Studios LLC.
          </p>
          <p className="text-xs text-muted-foreground">
            2340 E. Trinity Mills Road, Suite 300, Carrollton, TX, 75006
          </p>
          <p className="text-xs text-muted-foreground">
            Contact: support@greyrockai.app
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Terms;
